import type {
  OperatorUser,
  OrganizationMember,
  OrganizationMemberRole,
  OrganizationMemberStatus,
} from "./types";
import { formatUserLabel, formatUserSearchText } from "./userLabel";

export type OrganizationMemberOption = {
  value: string;
  label: string;
  searchText: string;
  avatarUrl?: string | null;
};

const toToken = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (typeof record.value === "string") return record.value;
    if (typeof record.name === "string") return record.name;
  }
  return String(value);
};

const normalizeEnumString = (value: unknown): string => {
  const token = toToken(value).trim();
  if (!token) return "";
  const lastSegment = token.includes(".") ? token.split(".").pop() || token : token;
  return lastSegment.trim().toLowerCase();
};

export const normalizeOrganizationMemberRole = (
  role: unknown,
): OrganizationMemberRole => {
  const token = normalizeEnumString(role);
  if (token === "owner") return "owner";
  if (token === "admin") return "admin";
  if (token === "operator" || token === "member" || token === "staff") {
    return "operator";
  }
  return "operator";
};

export const normalizeOrganizationMemberStatus = (
  status: unknown,
): OrganizationMemberStatus => {
  const token = normalizeEnumString(status);
  if (!token || token === "none" || token === "active") return "none";
  if (token === "pending" || token === "invite_pending") return "pending";
  if (token === "accepted") return "accepted";
  if (token === "revoked") return "revoked";
  if (token === "expired") return "expired";
  return "none";
};

export const organizationMemberRoleToApi = (
  role: OrganizationMemberRole | string,
): string => {
  return normalizeOrganizationMemberRole(role);
};

export const organizationMemberRoleLabel = (
  role?: OrganizationMemberRole | null,
): string => {
  if (!role) return "—";
  if (role === "owner") return "Owner";
  if (role === "admin") return "Admin";
  return "Operator";
};

export const organizationMemberStatusLabel = (
  status?: OrganizationMemberStatus | null,
): string => {
  if (!status) return "—";
  if (status === "none") return "Active";
  if (status === "pending") return "Invite pending";
  if (status === "accepted") return "Accepted";
  if (status === "revoked") return "Revoked";
  if (status === "expired") return "Expired";
  return "—";
};

export const normalizeOrganizationMember = (member: any): OrganizationMember => ({
  photoUrl: member?.photo_url ?? member?.photoUrl ?? null,
  canApproveCommissionPayments: member?.canApproveCommissionPayments ?? member?.can_approve_commission_payments,
  canLinkExpenseAssociations: member?.canLinkExpenseAssociations ?? member?.can_link_expense_associations,
  id:
    member?.id ??
    member?.inviteId ??
    member?.invite_id ??
    member?.userId ??
    member?.user_id ??
    null,
  user_id: member?.user_id ?? member?.userId ?? null,
  invitee_email: member?.invitee_email ?? member?.inviteeEmail ?? "",
  first_name: member?.first_name ?? member?.firstName ?? null,
  last_name: member?.last_name ?? member?.lastName ?? null,
  phone: member?.phone ?? null,
  role: normalizeOrganizationMemberRole(member?.role),
  status: normalizeOrganizationMemberStatus(member?.status),
  expires_at: member?.expires_at ?? member?.expiresAt ?? null,
});

export const canManageOrganizationMembers = (
  role?: OrganizationMemberRole | null,
): boolean => role === "owner" || role === "admin";

export const organizationMemberUserId = (member: OrganizationMember): string =>
  member.userId || member.user_id || "";

export const organizationMemberUser = (
  member?: OrganizationMember,
): OperatorUser | undefined => {
  if (!member) return undefined;
  const id = organizationMemberUserId(member);
  if (!id) return undefined;
  return {
    id,
    photoUrl: member.photoUrl,
    first_name: member.first_name ?? member.firstName ?? undefined,
    last_name: member.last_name ?? member.lastName ?? undefined,
    email: member.invitee_email ?? member.inviteeEmail ?? undefined,
  };
};

export const organizationMemberLabel = (member?: OrganizationMember): string =>
  formatUserLabel(organizationMemberUser(member));

export const organizationMemberSearchText = (
  member?: OrganizationMember,
): string => formatUserSearchText(organizationMemberUser(member));

export const isAssignableOrganizationMember = (
  member: OrganizationMember,
): boolean =>
  Boolean(organizationMemberUserId(member)) &&
  (member.status === "none" || member.status === "accepted") &&
  (member.role === "owner" || member.role === "admin" || member.role === "operator");

export const assignableOrganizationMembers = (
  members: OrganizationMember[],
): OrganizationMember[] => members.filter(isAssignableOrganizationMember);

export const organizationMemberToOperator = (
  member: OrganizationMember,
): OperatorUser | null => organizationMemberUser(member) || null;

export const organizationMemberOption = (
  member: OrganizationMember,
): OrganizationMemberOption | null => {
  const id = organizationMemberUserId(member);
  if (!id) return null;
  return {
    value: id,
    avatarUrl: member.photoUrl ?? null,
    label: organizationMemberLabel(member),
    searchText: organizationMemberSearchText(member),
  };
};

export const assignableOrganizationMemberOptions = (
  members: OrganizationMember[],
): OrganizationMemberOption[] =>
  assignableOrganizationMembers(members)
    .map(organizationMemberOption)
    .filter((option): option is OrganizationMemberOption => Boolean(option));

export const organizationMembersByUserId = (
  members: OrganizationMember[],
): Record<string, OrganizationMember> =>
  members.reduce<Record<string, OrganizationMember>>((acc, member) => {
    const id = organizationMemberUserId(member);
    if (id) acc[id] = member;
    return acc;
  }, {});
